"use client";
import { imagePath } from "@/lib/userUtils";
import { cn } from "@/lib/utils";
import { CategoriesQuery, WebSettingQuery } from "@/types/generated";
import { ApolloQueryResult } from "@apollo/client";
import Image from "next/image";
import Link from "next/link";
import { Suspense, useEffect, useState } from "react";
import Search from "../algolia-search";
import { ModeToggle } from "../mode-toggle";
import TradingViewTicker from "../moneyview/ticker";
import BreakingNewsSlider from "./animated-bn";
import { MainSidebar } from "./main-sidebar";
import { UserDropdown } from "./user-dropdown";
import { useIsMobile } from "@/hooks/use-mobile";

type MainNavbarProps = {
  webSettings: ApolloQueryResult<WebSettingQuery>;
  categories: ApolloQueryResult<CategoriesQuery>;
};

const MainNavbar = ({ categories, webSettings }: MainNavbarProps) => {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hasScrolledPastThreshold, setHasScrolledPastThreshold] =
    useState(false);
  const scrollThreshold = 250;

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < scrollThreshold) {
        setIsVisible(true);
        setHasScrolledPastThreshold(false);
      } else {
        setHasScrolledPastThreshold(true);
      }

      if (hasScrolledPastThreshold) {
        if (currentScrollY + window.innerHeight >= document.body.scrollHeight) {
          setIsVisible(true);
        } else if (currentScrollY > lastScrollY) {
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY, hasScrolledPastThreshold]);

  useEffect(() => {
    if (isVisible) {
      document.body.classList.remove("navbar-hidden");
    } else {
      document.body.classList.add("navbar-hidden");
    }
  }, [isVisible]);

  return (
    <>
      <div
        className={cn(
          "z-[100] border-b border-neutral-800  fixed top-0 w-full transition-transform duration-300 pr-[var(--removed-body-scroll-bar-size)]",
          {
            "translate-y-0": isMobile ? true : isVisible,
            "-translate-y-full": isMobile ? false : !isVisible,
          }
        )}
      >
        <nav className={cn("bg-neutral-900 text-white")}>
          <div className="container mx-auto flex justify-between items-center px-4 py-2">
            <div className="lg:min-w-[100px]">
              <MainSidebar categories={categories.data.categories} />
            </div>
            <div className="flex-1 flex items-center justify-start lg:justify-center">
              <Link href="/" className="text-xl font-bold">
                <Image
                  src={imagePath(webSettings.data.webSetting?.logoDark?.url)}
                  alt="MoneyOval"
                  quality={100}
                  width={150}
                  height={30}
                  loading="eager"
                  priority
                  className="cursor-pointer w-auto h-[21px] lg:h-[30px]"
                />
              </Link>
            </div>
            <div className="flex items-center gap-4 lg:min-w-[100px] justify-end">
              <ModeToggle />
              <Suspense>
                <UserDropdown />
              </Suspense>
            </div>
          </div>
        </nav>
      </div>
      <div className="min-h-12 bg-neutral-900 mt-[52px] lg:mt-[53px]">
        <div className="container mx-auto px-4 flex justify-center items-center">
          <TradingViewTicker />
        </div>
      </div>

      <div className="shadow-md dark:border-b dark:border-neutral-900">
        <div className="container mx-auto py-3 px-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-1 items-center justify-start text-neutral-900 dark:text-neutral-400 gap-1 line-clamp-1 ">
              <span className="icon-[tabler--news] text-lg"></span>
              <span className="font-bold mr-1 hidden lg:block">
                BREAKING NEWS
              </span>
              <span className="icon-[tabler--minus-vertical] text-lg"></span>
              <BreakingNewsSlider />
            </div>
            <div className="flex items-center justify-end cursor-pointer">
              <Search />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MainNavbar;
